// ============================================================================
// Componente: AutomacaoVRTransporte.js
// Objetivo: Interface para automação da gestão de VR Transporte (RH).
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import { 
    ArrowLeft, FileSpreadsheet, Play, 
    CheckCircle, AlertCircle, Loader2, CalendarDays, HelpCircle, Info
} from 'lucide-react';
import HelpCenter from '../../HelpCenter';

const AutomacaoVRTransporte = ({ embedded = false }) => {
    const navigate = useNavigate();
    
    // Controle de Layout
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);

    // Estados da Automação
    const [arquivo, setArquivo] = useState(null);
    // Competência (AAAA-MM) substituiu o preço da gasolina em 2026-08-18: o preço passou
    // a vir da coluna VALOR PASSAGEM da POPULIS. O mês continua sendo necessário porque
    // é ele que decide quais férias programadas tornam o valor do VR proporcional.
    // ⚠️⚠️ Desde 2026-08-31 é o MÊS QUE A RECARGA COBRE, não o mês em que a planilha é
    // gerada — ela sai entre os dias 24 e 28 já para o mês seguinte (a planilha de
    // 25/08 é informada como 09/AAAA). A janela de dois meses foi removida: ela
    // descontava o mesmo período em duas rodadas.
    const [competencia, setCompetencia] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [status, setStatus] = useState('idle'); // idle, processing, success, error
    const [errorMessage, setErrorMessage] = useState('');
    const [resumo, setResumo] = useState(null);

    // Estados de Ajuda
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    
    // Estados de Progresso
    const [progress, setProgress] = useState(0);
    const [statusText, setStatusText] = useState('Aguardando inicialização...');

    // Regras de Perfil do Usuário
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const nomeUsuario = user?.nome ? user.nome.split(' ')[0] : 'Colaborador';

    // Estilos Auxiliares (Tom predominante Âmbar para diferenciar do Empréstimo)
    const corModulo = '#d97706'; 

    useEffect(() => {
        const handleResize = () => { 
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile) setIsCollapsed(true);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getMarginLeft = () => isMobile ? '0px' : (isCollapsed ? '80px' : '260px');

    // Handlers para o arquivo
    const handleArquivoChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setArquivo(e.target.files[0]);
            resetState();
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.currentTarget.style.borderColor = corModulo;
        e.currentTarget.style.backgroundColor = '#ffe4e6';
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.currentTarget.style.borderColor = '#cbd5e1';
        e.currentTarget.style.backgroundColor = '#f8fafc';
    };

    const handleDropArquivo = (e) => {
        e.preventDefault();
        e.currentTarget.style.borderColor = '#cbd5e1';
        e.currentTarget.style.backgroundColor = '#f8fafc';
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            setArquivo(e.dataTransfer.files[0]);
            resetState();
        }
    };

    const resetState = () => {
        setStatus('idle');
        setErrorMessage('');
        setResumo(null);
        setProgress(0);
        setStatusText('Aguardando inicialização...');
    };

    const iniciarAutomacao = async (event) => {
        event.preventDefault();
        resetState();

        if (!arquivo) {
            setErrorMessage('A planilha POPULIS.xlsx é obrigatória.');
            setStatus('error');
            return;
        }

        if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(competencia)) {
            setErrorMessage('Informe o mês da recarga (o mês que o crédito vai cobrir).');
            setStatus('error');
            return;
        }

        setIsProcessing(true);
        setStatus('processing');
        setProgress(45);
        setStatusText("Enviando dados e processando a planilha...");

        const formData = new FormData();
        formData.append('populis', arquivo);
        formData.append('competencia', competencia);

        try {
            const token = localStorage.getItem('token') || localStorage.getItem('authToken');
            const response = await fetch('/api/automacao-rh/vr-transporte', {
                method: 'POST',
                headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                body: formData,
            });

            if (!response.ok) {
                let detalhe = 'Não foi possível gerar a planilha.';
                try {
                    const json = await response.json();
                    detalhe = json?.detalhe || json?.erro || detalhe;
                } catch (_) {}
                throw new Error(detalhe);
            }

            setProgress(85);
            setStatusText("Processamento concluído. Verificando dados e baixando...");

            let resumoHeader = null;
            try {
                const header = response.headers.get('X-VR-Transporte-Resumo');
                if (header) resumoHeader = JSON.parse(decodeURIComponent(header));
            } catch (_) {}

            // ==========================================
            // DOWNLOAD DO FICHEIRO ZIP
            // ==========================================
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // AQUI ESTÁ A ALTERAÇÃO (de .xlsx para .zip)
            link.download = `VR_Transporte_Abscard_${new Date().toISOString().slice(0, 10)}.zip`;
            
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            if (resumoHeader) {
                setResumo(resumoHeader);
                const qtdAvisos = Number(resumoHeader.avisos || 0);
                // O mês e os dias úteis aparecem no resultado de propósito: é o jeito
                // mais barato de perceber que a recarga foi gerada para o mês errado
                // (o campo é o mês COBERTO, não o mês em que se gera a planilha).
                const mesTexto = resumoHeader.competencia
                    ? ` · recarga de ${resumoHeader.competencia.split('-').reverse().join('/')}`
                    : '';
                const diasTexto = resumoHeader.diasUteisMes ? ` (${resumoHeader.diasUteisMes} dias úteis)` : '';
                setStatusText(`Sucesso para ${resumoHeader.usuarios || 0} funcionário(s)${mesTexto}${diasTexto}. ${qtdAvisos > 0 ? 'Existem avisos.' : 'Sem avisos.'}`);
            } else {
                setStatusText("Download concluído com sucesso!");
            }

            setProgress(100);
            setStatus('success');
            
        } catch (error) {
            console.error("Erro na requisição:", error);
            setStatus('error');
            setErrorMessage(error.message || 'Erro de conexão ou falha no processamento.');
            setStatusText("Falha na automação.");
        } finally {
            setIsProcessing(false);
        }
    };

    const getProgressBarColor = () => {
        if (status === 'error') return '#ef4444'; 
        if (status === 'success') return '#10b981'; 
        return corModulo; 
    };

    return (
        <div style={embedded ? { width: '100%' } : { display: 'flex', minHeight: '100vh', backgroundColor: '#f4f6f8' }}>
            {!embedded && <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}

            <div style={{ flex: 1, marginLeft: embedded ? 0 : getMarginLeft(), transition: 'margin-left 0.3s ease', width: '100%' }}>

                {/* CABEÇALHO PREMIUM */}
                {!embedded && (
                <div style={{
                    height: '85px', backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', display: 'flex',
                    alignItems: 'center', justifyContent: 'space-between',
                    padding: isMobile ? '0 20px 0 60px' : '0 40px', position: 'sticky', top: 0, zIndex: 20
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <button onClick={() => navigate('/automacoes')} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
                                <ArrowLeft size={14} /> Voltar
                            </button>
                            <span style={{ margin: '0 4px' }}>/</span> Automação & Processos <span style={{ margin: '0 4px' }}>/</span> RH
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffe4e6', padding: '6px', borderRadius: '8px' }}>
                                    <FileSpreadsheet size={24} color={corModulo} />
                                </div>
                                VR Transporte
                            </h2>
                            <button
                                type="button"
                                onClick={() => setIsHelpOpen(true)}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px', backgroundColor: '#ffe4e6', color: corModulo, border: `1px solid #fecaca`, borderRadius: '99px', fontWeight: '600', cursor: 'pointer', fontSize: '12px', transition: 'all 0.2s' }}
                            >
                                <HelpCircle size={14} /> Como Utilizar
                            </button>
                        </div>
                    </div>
                    {!isMobile && (
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#475569' }}>
                            Olá, {nomeUsuario} 👋
                        </span>
                    )}
                </div>
                )}

                <div style={{ padding: isMobile ? '20px' : '40px' }} className="max-w-5xl mx-auto">
                    
                    {/* CARTÃO PRINCIPAL */}
                    <div style={{ backgroundColor: '#fff', padding: isMobile ? '20px' : '30px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', marginBottom: '30px' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>Arquivos e Parâmetros</h3>
                        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px' }}>
                            A automação filtra a linha 5977, mantém os CPFs oficiais de VR e preserva EMPRESA e PRODUTOS do template original.
                        </p>

                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.2fr 0.8fr', gap: '24px', marginBottom: '24px' }}>
                            
                            {/* COLUNA ESQUERDA: UPLOAD */}
                            <div 
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDropArquivo}
                                style={{ 
                                    border: '2px dashed #cbd5e1', borderRadius: '12px', padding: '40px 20px', 
                                    textAlign: 'center', backgroundColor: '#f8fafc', cursor: 'pointer',
                                    transition: 'all 0.2s ease', position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'center'
                                }}
                            >
                                <input 
                                    type="file" 
                                    accept=".xlsx, .xls" 
                                    onChange={handleArquivoChange}
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                                    disabled={isProcessing}
                                />
                                
                                {!arquivo ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ backgroundColor: '#ffe4e6', padding: '16px', borderRadius: '50%', color: corModulo }}>
                                            <FileSpreadsheet size={28} />
                                        </div>
                                        <div>
                                            <p style={{ margin: '0 0 6px 0', fontWeight: '600', color: '#334155', fontSize: '15px' }}>Planilha POPULIS GVBUS</p>
                                            <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>Arraste ou clique (Apenas .xlsx)</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ backgroundColor: '#ecfdf5', padding: '16px', borderRadius: '50%', color: '#10b981' }}>
                                            <CheckCircle size={28} />
                                        </div>
                                        <div>
                                            <p style={{ margin: '0 0 6px 0', fontWeight: '700', color: '#0f172a', fontSize: '15px', wordBreak: 'break-all' }}>{arquivo.name}</p>
                                            <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>{(arquivo.size / 1024 / 1024).toFixed(2)} MB</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* COLUNA DIREITA: COMPETÊNCIA E REGRAS */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <label style={{ display: 'block' }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>
                                        <CalendarDays size={18} color={corModulo} /> Mês da recarga
                                    </span>
                                    <input
                                        type="month"
                                        value={competencia}
                                        onChange={(e) => setCompetencia(e.target.value)}
                                        disabled={isProcessing}
                                        style={{
                                            width: '100%', padding: '14px 16px', borderRadius: '10px',
                                            border: '1px solid #cbd5e1', fontSize: '15px', color: '#1e293b',
                                            outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box',
                                            fontVariantNumeric: 'tabular-nums',
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = corModulo}
                                        onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                                    />
                                    <span style={{ display: 'block', marginTop: '6px', fontSize: '12px', color: '#94a3b8', lineHeight: '1.5' }}>
                                        É o mês que o crédito vai <strong>cobrir</strong>, não o mês em que você gera a planilha: gerando entre os dias 24 e 28, informe o <strong>mês seguinte</strong>. É dele que saem os dias úteis e as férias descontadas. O preço da gasolina não é mais digitado — vem da coluna <strong>VALOR PASSAGEM</strong> da planilha.
                                    </span>
                                </label>

                                <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px' }}>
                                    <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '700', color: '#475569', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Info size={16} /> Resumo das Regras
                                    </h4>
                                    <ul style={{ margin: 0, paddingLeft: '20px', color: '#64748b', fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <li>Filtro: Registros da POPULIS com <strong>LINHA 5977</strong>.</li>
                                        <li>Cálculo: <strong>CG (litros)</strong> × <strong>VALOR PASSAGEM</strong>, os dois lidos da planilha.</li>
                                        <li>Férias programadas no mês da recarga deixam o valor <strong>proporcional</strong> aos dias úteis do mês (segunda a sexta, menos feriados federais).</li>
                                        <li>Sem CG ou sem VALOR PASSAGEM: fica <strong>fora do arquivo</strong> e aparece nos avisos.</li>
                                        <li>Formatação: Colunas bancárias como texto (preserva zeros).</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* MENSAGENS DE ERRO E ALERTAS */}
                        {errorMessage && status === 'error' && (
                            <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#ef4444', padding: '12px 16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '14px', fontWeight: '500' }}>
                                <AlertCircle size={18} /> {errorMessage}
                            </div>
                        )}

                        {/* AVISOS DA VALIDAÇÃO — agrupados por tipo.
                            Não é enfeite: quem aparece como "Sem dados para calcular o VR" ficou
                            FORA do arquivo da Abscard, ou seja, não vai receber neste mês. */}
                        {resumo?.listaAvisos?.length > 0 && status === 'success' && (
                            <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', color: '#b45309', padding: '16px', borderRadius: '8px', marginBottom: '24px', fontSize: '14px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', marginBottom: '12px' }}>
                                    <AlertCircle size={18} /> Avisos de Validação ({resumo.avisos})
                                </div>

                                {Object.entries(
                                    resumo.listaAvisos.reduce((grupos, aviso) => {
                                        const tipo = aviso.tipo || 'Aviso';
                                        (grupos[tipo] = grupos[tipo] || []).push(aviso);
                                        return grupos;
                                    }, {})
                                ).map(([tipo, itens]) => (
                                    <div key={tipo} style={{ marginBottom: '12px' }}>
                                        <p style={{ margin: '0 0 6px 0', fontWeight: '600', fontSize: '13px' }}>{tipo} ({itens.length})</p>
                                        <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', color: '#92400e', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            {itens.map((item, idx) => (
                                                <li key={`${tipo}-${idx}`}>
                                                    <span style={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>{item.cpf}</span>
                                                    {item.nome ? ` — ${item.nome}` : ''}
                                                    {item.detalhe ? `: ${item.detalhe}` : ''}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}

                                {resumo.avisos > resumo.listaAvisos.length && (
                                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', fontStyle: 'italic' }}>
                                        …e mais {resumo.avisos - resumo.listaAvisos.length} aviso(s) não listado(s) aqui.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* BOTÃO DE AÇÃO */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button 
                                onClick={iniciarAutomacao}
                                disabled={isProcessing || !arquivo}
                                style={{ 
                                    display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', 
                                    backgroundColor: isProcessing || !arquivo ? '#cbd5e1' : '#0f172a', 
                                    color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', 
                                    fontSize: '15px', cursor: isProcessing || !arquivo ? 'not-allowed' : 'pointer',
                                    transition: 'background-color 0.2s, transform 0.1s', 
                                    boxShadow: '0 4px 6px -1px rgba(15, 23, 42, 0.2)'
                                }}
                            >
                                {isProcessing ? (
                                    <><Loader2 size={18} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} /> Processando...</>
                                ) : (
                                    <><Play size={18} /> Gerar Planilha VR</>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* CARD DE PROGRESSO E STATUS */}
                    {status !== 'idle' && (
                        <div style={{ 
                            backgroundColor: '#fff', 
                            borderRadius: '16px', 
                            padding: '24px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                            border: '1px solid #e2e8f0'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    {status === 'processing' && <Loader2 size={24} color="#0ea5e9" className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />}
                                    {status === 'success' && <CheckCircle size={24} color="#10b981" />}
                                    {status === 'error' && <AlertCircle size={24} color="#ef4444" />}
                                    
                                    <div>
                                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>Status da Execução</h4>
                                        <p style={{ margin: 0, fontSize: '14px', color: '#64748b', marginTop: '2px' }}>{statusText}</p>
                                    </div>
                                </div>
                                <span style={{ fontSize: '20px', fontWeight: '800', color: getProgressBarColor() }}>
                                    {progress}%
                                </span>
                            </div>

                            <div style={{ 
                                width: '100%', height: '12px', backgroundColor: '#f1f5f9', 
                                borderRadius: '99px', overflow: 'hidden'
                            }}>
                                <div style={{ 
                                    width: `${progress}%`, height: '100%', 
                                    backgroundColor: getProgressBarColor(),
                                    borderRadius: '99px',
                                    transition: 'width 0.5s ease-in-out, background-color 0.3s ease'
                                }} />
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* CHAMADA DO MANUAL COM FILTRO PARA VR */}
            <HelpCenter 
                isOpen={isHelpOpen} 
                onClose={() => setIsHelpOpen(false)} 
                secaoInicial="vr"
                showOnlyIds={['vr']}
            />
        </div>
    );
};

export default AutomacaoVRTransporte;